import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Link,
  Alert,
} from '@mui/material';
import { type LoginPayload } from '../utils/api';
import { LOGO_URL } from '../constants/brand';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { loginUser } from '../features/auth/authSlice';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { status, error: authError, token } = useAppSelector((state) => state.auth);

  const [formValues, setFormValues] = useState<LoginPayload>({
    email: '',
    password: '',
  });

  const isLoading = status === 'loading';

  useEffect(() => {
    if (token) {
      navigate('/patients', { replace: true });
    }
  }, [token, navigate]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await dispatch(loginUser(formValues)).unwrap();
      navigate('/patients');
    } catch {
      // Error notifications are handled within the thunk via toast
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        bgcolor: 'background.default',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 480,
          p: 5,
          borderRadius: 1,
          border: '1px solid rgba(0,0,0,0.05)',
          boxShadow: '0 10px 40px rgba(0,0,0,0.04)',
          bgcolor: 'background.paper',
        }}
      >
        <Stack spacing={4}>
          <Box sx={{ textAlign: 'center' }}>
            <Box
              component="img"
              src={LOGO_URL}
              alt="Logo"
              sx={{ height: 70, mb: 3, objectFit: 'contain' }}
            />
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Log in to your account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Welcome back! Provide your details to continue.
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={2.5}>
              <TextField
                label="Email"
                name="email"
                type="email"
                required
                fullWidth
                autoComplete="email"
                value={formValues.email}
                onChange={handleChange}
              />

              <TextField
                label="Password"
                name="password"
                type="password"
                required
                fullWidth
                autoComplete="current-password"
                value={formValues.password}
                onChange={handleChange}
              />

              {authError && <Alert severity="error">{authError}</Alert>}

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{
                  py: 1.5,
                  fontSize: 16,
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)',
                }}
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </Button>

              <Stack direction="row" justifyContent="space-between" mt={1}>
                <Link component={RouterLink} to="/register" variant="body2" fontWeight={600} underline="none">
                  Create account
                </Link>
                <Link component={RouterLink} to="/forgot-password" variant="body2" fontWeight={600} underline="none">
                  Forgot password?
                </Link>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default LoginPage;
